import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import SettingsPageLayout from '../settings/SettingsPageLayout';
import SlidePanel from '../shared/SlidePanel';
import useSites, { clearSitesCache } from './useSites';
import { sitesService, switchToSite, siteColour } from '../../_services/sites.service';
import { existingSalonOf } from './addLocation/locationCopy';
import { fetchPeople, fetchRoles, setAssignment, assignableRoles, errorText } from '../permissions/rolesStore';
import { permissionsService } from '../../_services/permissions.service';
import '../permissions/permissions.css';
import './multiLocation.css';

/**
 * Settings → Locations, on the settings design system (SettingsModern.css).
 * Three tabs: the list of locations (with a per-row menu and the one-off
 * "copy settings" action), team access, and the shared settings. Stock
 * transfers live under Catalogue and the cross-location gift card report
 * under Finance → All locations. The group is never named — the owner
 * "has locations" (spec §15b).
 */
const TABS = [
  { id: 'locations', label: 'Locations' },
  { id: 'access', label: 'Team access' },
  { id: 'shared', label: 'Shared settings' },
];

const ROLE_LABEL = { owner: 'Owner', manager: 'Site manager', area: 'Area manager' };
const roleLabel = (r) => ROLE_LABEL[r] || (r ? r.charAt(0).toUpperCase() + r.slice(1) : '');
const origin = () => (typeof window !== 'undefined' && window.location.origin) || '';

function LocationsPage(props) {
  const { sites, active, group, multi, canAddLocation, refresh, loading } = useSites();
  const [closing, setClosing] = useState(null);   // site being closed (confirm panel)
  const [tab, setTab] = useState('locations');
  const [copying, setCopying] = useState(false);
  // Adding a location is Bailey's location track (/add-location): billed
  // first, then set up at the new site. The old ?add=1 link goes there too.
  const history = props.history;
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('add') === '1' && history) history.replace('/add-location');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // One left part-way (on this device or another) picks up where it was.
  const [openIntent, setOpenIntent] = useState(null);
  useEffect(() => {
    if (!canAddLocation) return undefined;
    let alive = true;
    sitesService.currentLocationIntent()
      .then((it) => { if (alive) setOpenIntent(it && !existingSalonOf(it) ? it : null); })
      .catch(() => { /* the button just says Add a location */ });
    return () => { alive = false; };
  }, [canAddLocation]);
  const openName = openIntent && openIntent.answers && String(openIntent.answers.salonName || '').trim();
  const addLabel = openName ? `Carry on adding ${openName}` : 'Add a location';
  const startAdd = () => { if (history) history.push('/add-location'); };
  const visibleTabs = multi ? TABS : TABS.slice(0, 1);

  return (
    <SettingsPageLayout activeTab="locations" {...props}>
      <div className="catalogue-content-wrapper ml-page">
        {visibleTabs.length > 1 && (
          <div className="mlp-tabs" role="tablist">
            {visibleTabs.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={`mlp-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                {t.label}
                {t.id === 'locations' && sites.length > 0 && <span className="mlp-tab-count">{sites.length}</span>}
              </button>
            ))}
          </div>
        )}

        {tab === 'locations' && (
          <SitesTable onCloseSite={setClosing} sites={sites} loading={loading} multi={multi} canAddLocation={canAddLocation} onAdd={startAdd} addLabel={addLabel} onCopySettings={() => setCopying(true)} />
        )}
        {tab === 'access' && <TeamAccess sites={sites} history={props.history} />}
        {tab === 'shared' && <SharedSettings group={group} onSaved={() => { clearSitesCache(); refresh(); }} />}
      </div>
      {closing && <CloseLocationPanel site={closing} onClose={() => setClosing(null)} onClosed={(res) => { setClosing(null); clearSitesCache(); if (closing.isActive && res && res.fallback) switchToSite(res.fallback.salonId, '/settings/locations'); else refresh(); }} />}
      {copying && <CopySettingsPanel sites={sites} active={active} onClose={() => setCopying(false)} />}
    </SettingsPageLayout>
  );
}

/* ---------- shared bits on the settings design system ---------- */

function Switch({ on, onChange, label, disabled }) {
  return (
    <button
      type="button"
      className={`ds-toggle${on ? ' ds-toggle--on' : ''}${disabled ? ' ds-toggle--disabled' : ''}`}
      onClick={disabled ? undefined : () => onChange(!on)}
      disabled={disabled}
      role="switch"
      aria-checked={!!on}
      aria-label={label}
    >
      <span className="ds-toggle-knob" />
    </button>
  );
}

function ToggleRow({ title, help, on, onChange, children }) {
  return (
    <div className="bu-toggle-row">
      <div className="bu-toggle-text">
        <span>{title}</span>
        {help && <span className="bu-hint">{help}</span>}
      </div>
      <div className="bu-toggle-controls">
        {children || <Switch on={!!on} onChange={onChange} label={title} />}
      </div>
    </div>
  );
}

function Modes({ options, value, onChange, className = '' }) {
  return (
    <div className={`bu-modes ${className}`}>
      {options.map(([id, title, desc]) => (
        <button key={id} type="button" className={`bu-mode${value === id ? ' is-active' : ''}`} onClick={() => onChange(id)} aria-pressed={value === id}>
          <span className="bu-mode-radio" aria-hidden />
          <span className="bu-mode-text"><span className="bu-mode-title">{title}</span><span className="bu-mode-desc">{desc}</span></span>
        </button>
      ))}
    </div>
  );
}

function Card({ title, sub, action, children }) {
  return (
    <div className="settings-card">
      {(title || action) && (
        <div className="settings-card-header ml-card-header">
          <div>
            {title && <h2>{title}</h2>}
            {sub && <p>{sub}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function SiteName({ id, name }) {
  return <span className="ml-site"><span className="ml-dot" style={{ background: siteColour(id) }} />{name}</span>;
}

function Empty({ title, sub, action }) {
  return (
    <div className="ml-empty">
      <p className="ml-empty-title">{title}</p>
      {sub && <p className="ml-empty-sub">{sub}</p>}
      {action}
    </div>
  );
}

/**
 * Row actions menu ("⋯"). Rendered through a portal so the table's
 * horizontal scroll wrapper never clips it.
 */
function RowMenu({ label, items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const menu = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = btn.current && btn.current.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    };
    place();
    const onDown = (e) => { if (menu.current && !menu.current.contains(e.target) && btn.current && !btn.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);
  const visible = items.filter((i) => i && !i.hidden);
  if (!visible.length) return null;
  return (
    <>
      <button ref={btn} type="button" className="ml-menu-btn" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
      </button>
      {open && pos && ReactDOM.createPortal(
        <div ref={menu} className="ml-menu" role="menu" style={{ top: pos.top, right: pos.right }}>
          {visible.map((i) => (
            <button key={i.label} type="button" role="menuitem" className="ml-menu-item" onClick={() => { setOpen(false); i.onClick(); }}>{i.label}</button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

/* ---------- Close a location ---------- */

function CloseLocationPanel({ site, onClose, onClosed }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    sitesService.closeLocationPreview(site.salonId).then((p) => { if (alive) setPreview(p); }).catch((e) => { if (alive) setError(e.message || 'Could not check this location'); });
    return () => { alive = false; };
  }, [site.salonId]);
  const confirmOk = typed.trim().toLowerCase() === String(site.salonName || '').trim().toLowerCase();
  const run = async () => {
    setBusy(true); setError(null);
    try { const res = await sitesService.closeLocation(site.salonId); onClosed(res); }
    catch (e) { setError(e.message || 'Could not close this location'); setBusy(false); }
  };
  return (
    <SlidePanel title={`Close ${site.salonName}`} onClose={onClose}
      footer={<><button type="button" className="sp-btn-secondary" onClick={onClose} disabled={busy}>Keep it open</button><button type="button" className="sp-btn-primary" style={{ background: '#B14438' }} onClick={run} disabled={busy || !preview || !preview.canClose || !confirmOk}>{busy ? 'Closing…' : 'Close location'}</button></>}>
      {!preview && !error && <p className="bu-hint">Checking what this touches…</p>}
      {error && <p className="ml-wizard-error">{error}</p>}
      {preview && (
        <div className="ml-close">
          {preview.isLastLocation && <p className="ml-close-block">This is your only location, so it cannot be closed.</p>}
          {!preview.isLastLocation && preview.upcomingBookings > 0 && <p className="ml-close-block">{preview.upcomingBookings} upcoming booking{preview.upcomingBookings === 1 ? '' : 's'} still on the diary here. Move or cancel them first: closing never touches a client's booking.</p>}
          <p className="ml-close-lead">Closing this location is reversible only by Beu support. Here is what changes:</p>
          <ul className="ml-close-list">
            <li>It disappears from your switcher, dashboard and reports. Past sales stay in your history.</li>
            <li>{preview.staff} team record{preview.staff === 1 ? '' : 's'} here {preview.staff === 1 ? 'is' : 'are'} marked as left this location. Nobody's account is deleted.</li>
            {preview.staffOnlyHere > 0 && <li>{preview.staffOnlyHere} of them work{preview.staffOnlyHere === 1 ? 's' : ''} only here and will have no location afterwards. Add them to another location first if they are staying.</li>}
            <li>{preview.subscriptionLine ? 'Its line comes off your invoice, pro-rated from today.' : 'It has no line on your invoice.'}</li>
            <li>Its online booking page stops taking bookings.</li>
            {preview.openTransfers > 0 && <li>{preview.openTransfers} stock transfer{preview.openTransfers === 1 ? '' : 's'} still in transit to or from here. Receive or cancel {preview.openTransfers === 1 ? 'it' : 'them'} under Catalogue → Transfers.</li>}
            {preview.fallback && <li>Anyone working here right now is moved to {preview.fallback.salonName}.</li>}
          </ul>
          {preview.canClose && (
            <label className="ml-close-confirm">
              <span>Type <strong>{site.salonName}</strong> to confirm</span>
              <input className="sp-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={site.salonName} autoComplete="off" />
            </label>
          )}
        </div>
      )}
    </SlidePanel>
  );
}

/* ---------- Locations ---------- */

function SitesTable({ sites, loading, multi, canAddLocation, onAdd, addLabel = 'Add a location', onCopySettings, onCloseSite }) {
  const [copied, setCopied] = useState(null);
  const copyLink = (s) => {
    const url = `${origin()}/book/${s.slug}`;
    const done = () => { setCopied(String(s.salonId)); setTimeout(() => setCopied(null), 1600); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, done); else done();
  };
  const actions = multi ? (
    <div className="ml-card-actions">
      {sites.length > 1 && <button type="button" className="bu-inline-cancel" onClick={onCopySettings}>Copy settings…</button>}
      {canAddLocation && <button type="button" className="bu-inline-save" onClick={onAdd}>{addLabel}</button>}
    </div>
  ) : null;
  return (
    <>
      <Card title="Your locations" sub="Every location keeps its own diary, team, prices and settings." action={actions}>
        {loading && !sites.length ? (
          <p className="bu-hint">Loading…</p>
        ) : (
          <div className="mlp-table-wrap">
            <table className="settings-table mlp-table">
              <thead><tr><th>Location</th><th>Booking link</th><th>Your role</th><th aria-hidden /></tr></thead>
              <tbody>
                {sites.map((s) => (
                  <tr key={String(s.salonId)}>
                    <td>
                      <div className="ml-cell-title"><SiteName id={s.salonId} name={s.salonName} />{s.isActive && <span className="settings-badge settings-badge-info">Current</span>}</div>
                      <div className="ml-cell-sub">{s.formattedAddress || 'Not set'}</div>
                    </td>
                    <td>
                      {s.slug ? (
                        <span className="ml-link-cell">
                          <a className="ml-link-text" href={`${origin()}/book/${s.slug}`} target="_blank" rel="noopener noreferrer">/book/{s.slug}</a>
                          {copied === String(s.salonId) && <span className="ml-copied">Copied</span>}
                        </span>
                      ) : <span className="ml-cell-sub">Not set</span>}
                    </td>
                    <td><span className={`settings-badge ${s.role === 'owner' || s.role === 'manager' ? 'settings-badge-default' : 'settings-badge-info'}`}>{roleLabel(s.role)}</span></td>
                    <td className="ml-td-action">
                      <RowMenu label={`Actions for ${s.salonName}`} items={[
                        { label: 'Switch to this location', onClick: () => switchToSite(s.salonId), hidden: !!s.isActive },
                        { label: 'Copy booking link', onClick: () => copyLink(s), hidden: !s.slug },
                        { label: 'Open booking page', onClick: () => window.open(`${origin()}/book/${s.slug}`, '_blank', 'noopener'), hidden: !s.slug },
                        { label: 'Copy settings to other locations…', onClick: onCopySettings, hidden: !s.isActive || sites.length < 2 },
                        { label: 'Close this location…', onClick: () => onCloseSite(s), hidden: s.role !== 'owner' || sites.length < 2 },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {!multi && (
        <Card title="Run more than one salon from this account" sub="Each location keeps its own diary, team, prices and settings, on the Beu salon plan. You’ll see the price before anything is charged.">
          {canAddLocation && <button type="button" className="bu-inline-save" onClick={onAdd}>{addLabel}</button>}
        </Card>
      )}
    </>
  );
}

/* ---------- Copy settings (one-off) ---------- */

const AREA_LABELS = {
  booking: ['Booking rules', 'No-show and reschedule policy, online booking window, intervals, guest booking'],
  payments: ['Payments', 'Payment methods, reader tipping, who pays fees, no-show fee payer'],
  branding: ['Branding', 'Email header and colour, image, bio'],
  vouchers: ['Gift cards', 'Sell at till and online, expiry'],
  comms: ['Automated messages', 'Reminder timing and channels'],
};

function CopySettingsPanel({ sites, active, onClose }) {
  const [areas, setAreas] = useState([]);
  const [targets, setTargets] = useState([]);
  const [state, setState] = useState('idle');
  const others = sites.filter((s) => !s.isActive);
  // Every other location starts ticked; the owner unticks the ones to skip.
  useEffect(() => { setTargets(others.map((o) => String(o.salonId))); }, [sites]); // eslint-disable-line react-hooks/exhaustive-deps
  const n = targets.length;
  const run = async () => {
    setState('busy');
    try { await sitesService.pushSettings(areas, targets); setState('done'); setTimeout(onClose, 1400); } catch (e) { setState('error'); }
  };
  return (
    <SlidePanel title="Copy settings to other locations" onClose={onClose} width={480} className="ml-panel"
      footer={<>
        <button type="button" className="sp-btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="sp-btn-primary" onClick={run} disabled={!areas.length || !n || state === 'busy' || state === 'done'}>
          {state === 'busy' ? 'Copying…' : state === 'done' ? 'Copied' : state === 'error' ? 'Could not copy, try again' : `Copy to ${n} location${n === 1 ? '' : 's'}`}
        </button>
      </>}>
      <p className="ml-panel-intro">Takes a copy of the settings below from <strong>{active ? active.salonName : 'this location'}</strong>. Each location can change them again afterwards.</p>
      <div className="settings-form-group">
        <label className="settings-label">What to copy</label>
        <div className="ml-panel-list">
          {Object.entries(AREA_LABELS).map(([k, [t, sub]]) => <ToggleRow key={k} title={t} help={sub} on={areas.includes(k)} onChange={(c) => setAreas(c ? areas.concat(k) : areas.filter((a) => a !== k))} />)}
        </div>
      </div>
      <div className="settings-form-group">
        <label className="settings-label">Copy to</label>
        <SiteChecklist sites={others} selected={targets} onChange={setTargets} />
      </div>
    </SlidePanel>
  );
}

/* ---------- Team access ---------- */

/**
 * Who has access at more than one location. Roles are defined under Team >
 * Permissions and set on the person's profile; this tab shows where each
 * person's role applies and lets the owner give someone access at more
 * locations (beu-node-v2 docs/permissions.md §4). Access at a location where
 * someone doesn't work is access only — never owner-level.
 */
function TeamAccess({ sites, history }) {
  const [people, setPeople] = useState(null);
  const [roles, setRoles] = useState([]);
  const [panel, setPanel] = useState(false);
  const [form, setForm] = useState({ userId: '', roleKey: '', salonIds: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  // The pool to pick from: everyone at any of the owner's locations.
  const [staff, setStaff] = useState(null);
  const [query, setQuery] = useState('');
  const here = String(localStorage.getItem('salonId') || '');
  const load = () => Promise.all([fetchPeople(), fetchRoles()])
    .then(([p, r]) => { setPeople(p); setRoles(r.roles); })
    .catch(() => setPeople([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!panel || staff) return;
    sitesService.groupStaff().then((r) => setStaff((r && r.staff) || [])).catch(() => setStaff([]));
  }, [panel, staff]);
  const roleName = (key) => (roles.find((r) => r.key === key) || {}).name || 'Standard';
  const nameOf = (id) => (sites.find((s) => String(s.salonId) === String(id)) || {}).salonName || '';
  // Anyone who can act somewhere besides here — including old-style manager
  // rows, which are owner-level and must never be invisible.
  const rows = (people || []).filter((p) => p.roleKey !== 'owner' && (p.accessOnly || (p.elsewhere || []).length));
  const current = (people || []).find((p) => String(p.userId) === String(form.userId));
  const pick = (userId) => {
    const p = (people || []).find((x) => String(x.userId) === String(userId));
    setForm({ userId: String(userId), roleKey: (p && p.roleKey !== 'owner' && p.roleKey) || 'standard', salonIds: (p && p.salonIds) || [] });
  };
  const save = async () => {
    if (!form.userId || !form.roleKey) return;
    setBusy(true); setError(null);
    try {
      await setAssignment(form.userId, { roleKey: form.roleKey, salonIds: form.salonIds });
      setForm({ userId: '', roleKey: '', salonIds: [] }); setQuery(''); setPanel(false); load();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const remove = async (userId) => {
    try { await permissionsService.removeElsewhere(userId); } finally { setConfirmRemove(null); load(); }
  };
  const person = staff && staff.find((p) => String(p.userId) === String(form.userId));
  return (
    <>
      <Card title="Team access" sub="Who can act at more than one location. Set what a role can do under Team > Permissions." action={<button type="button" className="bu-inline-save" onClick={() => { setError(null); setPanel(true); }}>Give access</button>}>
        {people === null ? (
          <p className="acc-note">Loading…</p>
        ) : !rows.length ? (
          <Empty title="No one has access at another location yet" sub="Give a team member their role at more of your locations. Their diary, bookings and pay stay where they work." />
        ) : (
          <div className="mlp-table-wrap">
            <table className="settings-table mlp-table">
              <thead><tr><th>Team member</th><th>Role</th><th>Locations</th><th aria-hidden /></tr></thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={String(m.userId)}>
                    <td><div className="ml-cell-title">{m.name}</div>{m.accessOnly && <div className="ml-cell-sub">Works at {(m.worksAt || []).map(nameOf).filter(Boolean).join(', ') || 'another location'}</div>}</td>
                    <td><span className="settings-badge settings-badge-info">{roleName(m.roleKey)}</span></td>
                    <td className="ml-cell-sub">
                      {nameOf(here)}{(m.elsewhere || []).map((e) => (
                        <span key={e.salonId}>, {nameOf(e.salonId)}{e.roleKey !== m.roleKey && <> ({roleName(e.roleKey)}{e.source === 'legacy-manager' ? ', old-style' : ''})</>}</span>
                      ))}
                    </td>
                    <td className="ml-td-action">
                      {confirmRemove === String(m.userId) ? (
                        <span className="perm-inline-confirm">
                          <button type="button" className="acc-edit perm-danger-link" onClick={() => remove(m.userId)}>Remove</button>
                          <button type="button" className="acc-edit" onClick={() => setConfirmRemove(null)}>Keep</button>
                        </span>
                      ) : (
                        <RowMenu label={`Actions for ${m.name}`} items={[
                          { label: 'Change role or locations', onClick: () => { setError(null); pick(m.userId); setPanel(true); } },
                          { label: 'Open their profile', onClick: () => history && history.push(`/staff/${m.userId}`), hidden: m.accessOnly },
                          { label: 'Remove access where they don\'t work', onClick: () => setConfirmRemove(String(m.userId)), hidden: !(m.elsewhere || []).some((e) => !e.worksThere) },
                        ]} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {panel && (
        <SlidePanel title="Access at more locations" onClose={() => setPanel(false)} width={480} className="ml-panel"
          footer={<><button type="button" className="sp-btn-secondary" onClick={() => setPanel(false)}>Cancel</button><button type="button" className="sp-btn-primary" onClick={save} disabled={!form.userId || !form.roleKey || busy}>{busy ? 'Saving…' : 'Save'}</button></>}>
          <div className="settings-form-group">
            <label className="settings-label">Team member</label>
            <input type="text" className="settings-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search your team" />
            <div className="ml-staff-pick">
              {staff === null ? (
                <div className="ml-staff-pick-empty">Loading your team…</div>
              ) : (() => {
                const q = query.trim().toLowerCase();
                const list = staff.filter((p) => !q || (p.name || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q));
                if (!list.length) return <div className="ml-staff-pick-empty">{staff.length ? 'No one matches.' : 'No other team members at your locations yet.'}</div>;
                return list.map((p) => {
                  const on = String(form.userId) === String(p.userId);
                  return (
                    <button key={String(p.userId)} type="button" className={`ml-staff-pick-row ${on ? 'active' : ''}`} onClick={() => pick(p.userId)}>
                      <span className="ml-staff-pick-name">{p.name}{p.jobTitle ? <span className="ml-cell-sub"> · {p.jobTitle}</span> : null}</span>
                      <span className="ml-staff-pick-sites">{(p.sites || []).map((st) => (
                        <span key={String(st.salonId)} className="ml-staff-pick-site"><span className="ml-dot" style={{ background: siteColour(st.salonId) }} />{st.salonName}</span>
                      ))}</span>
                    </button>
                  );
                });
              })()}
            </div>
          </div>
          {form.userId && (
            <div className="settings-form-group">
              <label className="settings-label">Role</label>
              <div className="bu-modes acc-modes">
                {assignableRoles(roles).map((r) => (
                  <button key={r.key} type="button" className={`bu-mode${form.roleKey === r.key ? ' is-active' : ''}`} onClick={() => setForm({ ...form, roleKey: r.key })} aria-pressed={form.roleKey === r.key}>
                    <span className="bu-mode-radio" aria-hidden />
                    <span className="bu-mode-text"><span className="bu-mode-title">{r.name}</span><span className="bu-mode-desc">{r.description}</span></span>
                  </button>
                ))}
              </div>
              {current && !current.accessOnly && <p className="acc-note">This changes {person ? person.name.split(' ')[0] + '\'s' : 'their'} role here too. <Link className="acc-link" to="/staff/permissions">What each role can do</Link></p>}
            </div>
          )}
          <div className="settings-form-group">
            <label className="settings-label">Also applies at</label>
            <SiteChecklist sites={sites.filter((s) => String(s.salonId) !== here)} selected={form.salonIds} onChange={(ids) => setForm({ ...form, salonIds: ids })} />
            <p className="acc-note">It always applies here, at {nameOf(here) || 'this location'}. Where they don't work, this lets them sign in there with this role; their diary, bookings and pay stay where they work.</p>
          </div>
          {error && <p className="acc-note perm-error">{error}</p>}
        </SlidePanel>
      )}
    </>
  );
}

/** Pick locations: one row each, name + address, drawn checkbox on the right. */
function SiteChecklist({ sites, selected, onChange }) {
  return (
    <div className="ml-panel-list ml-sites-pick" role="group">
      {sites.map((s) => {
        const id = String(s.salonId);
        const on = selected.includes(id);
        return (
          <button key={id} type="button" className={`ml-site-pick${on ? ' is-on' : ''}`} role="checkbox" aria-checked={on} onClick={() => onChange(on ? selected.filter((x) => x !== id) : selected.concat(id))}>
            <span className="ml-dot" style={{ background: siteColour(s.salonId) }} />
            <span className="ml-site-pick-text">
              <span className="ml-site-pick-name">{s.salonName}</span>
              {s.formattedAddress && <span className="ml-site-pick-sub">{s.formattedAddress}</span>}
            </span>
            <span className="ml-site-pick-box" aria-hidden="true">
              {on && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Shared settings ---------- */

const TOGETHER_MODES = [
  ['connected', 'Connected', 'Clients, gift cards and loyalty can be shared between your locations.'],
  ['separate', 'Run separately', 'Each location is its own business with its own clients, gift cards and loyalty. Payouts per legal entity are set under Payouts.'],
];

// With client sharing switched on: how much of a record follows the client.
const CLIENT_MODES = [
  ['also-visits', 'Where they visit', 'A chip on the client card and search across locations.'],
  ['shared', 'Everything', 'Notes and history too.'],
];

function SaveRow({ state, onSave, label = 'Save' }) {
  return (
    <div className="bu-inline-actions ml-save-row">
      {state === 'done' && <span className="ml-save-note">Saved</span>}
      {state === 'error' && <span className="ml-save-note ml-save-note--error">Could not save. Please try again.</span>}
      <button type="button" className="bu-inline-save" onClick={onSave} disabled={state === 'busy'}>{state === 'busy' ? 'Saving…' : label}</button>
    </div>
  );
}

function SharedSettings({ group, onSaved }) {
  const g = (group && group.settings) || {};
  // Each location keeps its own booking link. showOtherLocations adds the
  // sister locations ("Also at" row + Locations section) to every location's
  // booking page. franchiseMode = the locations run separately.
  const init = () => ({ vouchersRedeemAcrossSites: g.vouchersRedeemAcrossSites !== false, clientMode: g.clientMode || 'also-visits', shareClientNotes: !!g.shareClientNotes, loyaltyAcrossSites: !!g.loyaltyAcrossSites, showOtherSiteBookings: g.showOtherSiteBookings !== false, travelBufferMinutes: g.travelBufferMinutes || 0, franchiseMode: !!g.franchiseMode, showOtherLocations: !!g.showOtherLocations });
  const [v, setV] = useState(init);
  const [state, setState] = useState('idle');
  useEffect(() => { setV(init()); }, [group]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => { setState('busy'); try { await sitesService.updateSettings(v); setState('done'); onSaved(); setTimeout(() => setState('idle'), 2000); } catch (e) { setState('error'); } };
  if (!group) return <Card><Empty title="Add a second location to see shared settings" /></Card>;
  const separate = v.franchiseMode;
  return (
    <>
      <Card title="How your locations work together" sub="Pick how much your locations share. You can change this at any time.">
        <Modes className="ml-modes" options={TOGETHER_MODES} value={separate ? 'separate' : 'connected'} onChange={(id) => setV({ ...v, franchiseMode: id === 'separate' })} />
      </Card>

      <Card title="Booking pages" sub="Every location has its own booking link. Find them on the Locations tab.">
        <ToggleRow title="Show my other locations" help="Adds an 'Also at' row and a Locations section to each location's booking page, so a client can pick the one nearest them." on={v.showOtherLocations} onChange={(c) => setV({ ...v, showOtherLocations: c })} />
      </Card>

      {separate && <p className="ml-group-note">The settings below are off while your locations run separately.</p>}
      <div className={`ml-group${separate ? ' is-off' : ''}`} aria-disabled={separate}>
        <Card title="Shared between your locations" sub="What your locations share with each other. You can change these at any time." action={<Link className="ml-card-link" to="/finance?tab=locations">See gift card redemptions</Link>}>
          <ToggleRow title="Gift cards work at every location" help="On: a gift card sold at one location can be spent at any of them. Off: it only works where it was sold." on={v.vouchersRedeemAcrossSites} onChange={(c) => setV({ ...v, vouchersRedeemAcrossSites: c })} />
          <ToggleRow title="Share clients across locations" help="On: your team can find clients who visit your other locations. Off: each location only sees its own clients." on={v.clientMode !== 'separate'} onChange={(c) => setV({ ...v, clientMode: c ? (v.clientMode !== 'separate' ? v.clientMode : 'also-visits') : 'separate', shareClientNotes: c ? v.shareClientNotes : false })} />
          {v.clientMode !== 'separate' && (
            <div className="bu-toggle-row ml-toggle-row--stack">
              <div className="bu-toggle-text">
                <span>How much is shared</span>
                <span className="bu-hint">How much of a client's record follows them between your locations.</span>
              </div>
              <Modes options={CLIENT_MODES} value={v.clientMode} onChange={(id) => setV({ ...v, clientMode: id })} />
            </div>
          )}
          {v.clientMode === 'shared' && <ToggleRow title="Share client notes" help="Notes written at one location are visible at the others. A self-employed stylist's private clients stay private." on={v.shareClientNotes} onChange={(c) => setV({ ...v, shareClientNotes: c })} />}
        </Card>

        <Card title="Loyalty">
          <ToggleRow title="One loyalty scheme for all locations" help="Clients earn and redeem the same card everywhere." on={v.loyaltyAcrossSites} onChange={(c) => setV({ ...v, loyaltyAcrossSites: c })} />
        </Card>

        <Card title="Team across locations">
          <ToggleRow title="Show when team members are booked at another location" help="Their bookings elsewhere show as grey blocks in the diary." on={v.showOtherSiteBookings} onChange={(c) => setV({ ...v, showOtherSiteBookings: c })} />
          <ToggleRow title="Travel time between locations" help="Minimum gap between a team member's bookings at different locations.">
            <div className="bu-inline-fee">
              <input type="number" min="0" max="240" step="5" value={v.travelBufferMinutes} onChange={(e) => setV({ ...v, travelBufferMinutes: e.target.value })} aria-label="Travel time in minutes" />
              <span>min</span>
            </div>
          </ToggleRow>
        </Card>
      </div>

      <SaveRow state={state} onSave={save} />
    </>
  );
}

export default connect((state) => ({ user: state.user }))(LocationsPage);
